import React from 'react';

import { Box, Grid } from '@mui/material';

import LoadingButton from '../../components/Button/LoadingButton';
import { AddShopifyStoreDialog } from '../../components/Dialogs/AddShopifyStore/AddShopifyStoreDialog';
import PageTitle from '../../components/PageTitle/PageTitle';
import { Gallery } from '../../components/Photoswipe/Photoswipe';
import config, { enableShopifyVenueID } from '../../config';
import { useVenueCreateShopifyConnectionMutation } from '../../hooks/reactQuery/shopify/useVenueCreateShopifyConnectionMutation';
import { useVenueGetShopifyStoreConnectionQuery } from '../../hooks/reactQuery/shopify/useVenueGetShopifyStoreConnectionQuery';
import useQueryParams from '../../hooks/useQueryParams';
import useVenueId from '../../hooks/useVenueId';
import { useAppSelector } from '../../redux/hooks';
import { generateShopifyOAuthUrl } from '../../utils/shopify';
import { VENUE_MANUAL_TAGS } from '../../utils/venue-tags';
import PortfolioPanel from './components/PortfolioPanel';
import ProfilePanel from './components/ProfilePanel';

const Profile: React.FC = () => {
  const venueID = useVenueId();
  const venueState = useAppSelector((state) => state.venue.value);

  const isOnlineBrand = venueState?.tags?.includes(VENUE_MANUAL_TAGS.Online);

  const { getQueryParam } = useQueryParams();
  const shop = getQueryParam('shop');
  const code = getQueryParam('code');

  const [connectShopifyStoreDialog, setConnectShopifyStoreDialog] = React.useState(false);

  const venueShopifyConnectionMutate = useVenueCreateShopifyConnectionMutation({});
  const hasMutatedRef = React.useRef(false);

  const { data: storeConnectionData, isFetching: isStoreConnectionFetching } =
    useVenueGetShopifyStoreConnectionQuery({
      venueID: venueID || '',
    });

  React.useEffect(() => {
    if (
      !shop ||
      !code ||
      !venueID ||
      hasMutatedRef.current ||
      storeConnectionData ||
      isStoreConnectionFetching
    ) {
      return;
    }

    if (venueID && !venueShopifyConnectionMutate.isSuccess) {
      venueShopifyConnectionMutate.mutate({
        shopAddress: shop,
        code,
        venueID: Number(venueID),
      });
    }

    hasMutatedRef.current = true;
  }, [
    shop,
    code,
    venueID,
    venueShopifyConnectionMutate.isSuccess,
    storeConnectionData,
    isStoreConnectionFetching,
  ]);

  const connectShopifyStore = (name: string) => {
    const shop = `${name}.myshopify.com`; // from user input in real scenario

    const { url, nonce } = generateShopifyOAuthUrl({
      shop,
      clientId: config.shopifyClientId,
      redirectUri: window.location.origin + '/profile',
    });

    localStorage.setItem('shopify_oauth_nonce', nonce); // for CSRF protection

    console.log(JSON.stringify(url));
    window.location.href = url;
  };

  return (
    <Box px={4} py={2} sx={{ backgroundColor: 'white', flex: 1 }}>
      <PageTitle title='Profile' />

      <Box mt={2} />

      <Gallery>
        <Grid container spacing={6}>
          <Grid item xs={4}>
            <ProfilePanel />
            {enableShopifyVenueID &&
              String(enableShopifyVenueID) === String(venueID) &&
              isOnlineBrand && (
                <Box mt={3}>
                  {storeConnectionData?.shopAddress || shop ? (
                    <LoadingButton
                      variant='contained'
                      onClick={() => {
                        window.open(
                          `https://${storeConnectionData?.shopAddress || shop}/admin/apps/${
                            config.shopifyClientId
                          }`,
                          '_blank',
                        );
                      }}
                      loading={isStoreConnectionFetching}
                    >
                      Go to your Shopify store
                    </LoadingButton>
                  ) : (
                    <LoadingButton
                      variant='contained'
                      onClick={() => setConnectShopifyStoreDialog(true)}
                      loading={isStoreConnectionFetching}
                    >
                      Connect your Shopify store
                    </LoadingButton>
                  )}
                </Box>
              )}
          </Grid>
          <Grid item xs={8}>
            <PortfolioPanel />
          </Grid>
        </Grid>
      </Gallery>

      <AddShopifyStoreDialog
        open={connectShopifyStoreDialog}
        onClose={() => setConnectShopifyStoreDialog(false)}
        onSubmitHandle={(name) => {
          connectShopifyStore(name);
          setConnectShopifyStoreDialog(false);
        }}
      />
    </Box>
  );
};

export default Profile;
